// Typed REST client for /api/v1. All requests go through Vite proxy in dev
// and through nginx in prod, so we use relative URLs.

import type {
  AdminAssignDevices,
  AdminAssignStages,
  AdminAssignStations,
  AdminPermissionRead,
  AdminResetPassword,
  AdminResetPin,
  AdminRoleRead,
  AdminRolePermissionsUpdate,
  AdminRotateNfc,
  AdminUserCreate,
  AdminUserRead,
  AdminUserUpdate,
  AuditEntry,
  AuditFacets,
  DeviceRead,
  ErpPullSettings,
  ErpPullSettingsPatch,
  ErpPullCycleResult,
  IntegrationOutboxRow,
  IntegrationOutboxStats,
  IntegrationOutboxStatus,
  InventoryBatch,
  InventoryBatchCreate,
  InventoryConsumptionEvent,
  InventoryMaterial,
  InventoryMaterialCreate,
  InventoryMaterialPatch,
  InventoryRoll,
  InventoryRollCreate,
  MetricDefinition,
  CreateMetricBody,
  UpdateMetricBody,
  OperationConfigPatch,
  StageConfigPatch,
  CreateStageBody,
  CreateOperationBody,
  CreateFlowBody,
  StageCreated,
  OperationCreated,
  FlowCreated,
  ProcessStageGraph,
  ExecutionComplete,
  ExecutionQC,
  ExecutionRead,
  ExecutionStart,
  ExecutionStatus,
  FlowStatus,
  FlowType,
  ISODateTime,
  MaterialItemCreate,
  MaterialItemRead,
  MaterialItemUpdate,
  MaterialItemScan,
  MaterialItemTrace,
  ManufacturingOrderCreate,
  ManufacturingOrderDetailed,
  ManufacturingOrderLaunchResponse,
  ManufacturingOrderPreviewRequest,
  ManufacturingOrderPreviewResponse,
  ManufacturingOrderPrintBatchRequest,
  ManufacturingOrderPrintBatchResponse,
  ManufacturingOrderReceiveResponse,
  ManufacturingOrderRead,
  ManufacturingOrderSource,
  ManufacturingOrderStatus,
  OrderProcess,
  ReferenceItem,
  ReferenceList,
  ManufacturingOrderUpdate,
  ShortFlowListResponse,
  ShortFlowAdvanceResponse,
  OperationRead,
  OperatorRole,
  AdminRoleCreate,
  OrderAdjustmentCreate,
  OrderAdjustmentRead,
  OrderStatus,
  Page,
  PrintLabelRequest,
  PrintLabelResponse,
  ProductionOrderCreate,
  ProductionOrderRead,
  ProductionOrderReadDetailed,
  ProductionOrderUpdate,
  ScanEventInput,
  ScanEventRead,
  ScanLookupRequest,
  ScanLookupResponse,
  StageRead,
  StageReadDetailed,
  StationAssignOperators,
  StationAssignmentRead,
  StationAssignmentReadDetailed,
  StationCreate,
  StationRead,
  StationReadDetailed,
  StationUpdate,
  TagCreate,
  TagKind,
  TagRead,
  TagTargetType,
  UUID,
} from "./types";

const BASE = "/api/v1";

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown> | null;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Киоск-режим: статический API-токен (mespat_…) вместо операторской сессии.
// Пока он установлен, запросы идут под ним, а машинерия refresh/redirect
// на /login отключена — киоск без логина не должен уводить на форму входа.
let staticApiToken: string | null = null;

export function setStaticApiToken(token: string | null): void {
  staticApiToken = token;
}

function authHeader(): Record<string, string> {
  if (staticApiToken !== null) {
    return { authorization: `Bearer ${staticApiToken}` };
  }
  // Read fresh on every call — token may have rotated via refresh.
  if (typeof localStorage === "undefined") return {};
  const token = localStorage.getItem("mes:auth:access");
  return token ? { authorization: `Bearer ${token}` } : {};
}

// Mirror the operator's chosen language to the backend so content names
// (stages/operations/flows) resolve in that locale (i18n S4).  Read fresh
// on every call — the switcher updates localStorage live.
function localeHeader(): Record<string, string> {
  if (typeof localStorage === "undefined") return {};
  const locale = localStorage.getItem("mes:locale");
  return locale ? { "x-locale": locale } : {};
}

// --- Silent refresh on 401 -----------------------------------------
// The access token has a short TTL (minutes) and the refresh token is
// the source of truth for "session is still alive" (its TTL is what
// REFRESH_TOKEN_TTL_SECONDS controls on the backend — bumped to 3 days
// so users don't get bounced mid-task).
//
// When any request returns 401 we attempt a single silent refresh and
// retry the original call.  Only if the refresh itself fails do we
// broadcast `mes:auth-expired` and let AuthContext kick the operator
// back to /login.  All concurrent 401s share one refresh promise so
// we don't spawn a stampede.
let refreshPromise: Promise<boolean> | null = null;
async function tryRefreshOnce(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  const rt = typeof localStorage !== "undefined"
    ? localStorage.getItem("mes:auth:refresh")
    : null;
  if (!rt) return false;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) return false;
      const tokens = (await res.json()) as {
        access_token: string;
        refresh_token: string;
      };
      localStorage.setItem("mes:auth:access", tokens.access_token);
      localStorage.setItem("mes:auth:refresh", tokens.refresh_token);
      window.dispatchEvent(new Event("mes:auth-change"));
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function doFetch(path: string, init: RequestInit): Promise<Response> {
  return fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      accept: "application/json",
      ...authHeader(),
      ...localeHeader(),
      ...init.headers,
    },
  });
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  let res = await doFetch(path, init);

  // Retry exactly once after a silent refresh.  The /auth/refresh route
  // itself is exempt — failing it means the session is genuinely gone.
  // Kiosk (static PAT) is exempt too: there is nothing to refresh.
  if (res.status === 401 && staticApiToken === null && path !== "/auth/refresh") {
    const refreshed = await tryRefreshOnce();
    if (refreshed) {
      res = await doFetch(path, init);
    }
  }

  if (res.status === 401 && staticApiToken === null) {
    // Refresh either wasn't possible or also failed — session is dead.
    window.dispatchEvent(new Event("mes:auth-expired"));
  } else if (res.status === 401) {
    // Киоск: статический токен отозван/протух — страница вернёт форму пароля.
    window.dispatchEvent(new Event("mes:kiosk-auth-failed"));
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  // Defensive parse: not every error response is JSON.  When the upstream
  // nginx serves a static 404 page, Hono's framework-default error path
  // returns plain "Not Found", or anything routes to a non-JSON 5xx —
  // the unparseable body would otherwise throw an opaque
  // "Unexpected non-whitespace character after JSON …" that hides the
  // real status.  Fall back to null body + status code in that case.
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }
  if (!res.ok) {
    const err =
      body && typeof body === "object" && "error" in body
        ? (body as { error?: { code?: string; message?: string; details?: Record<string, unknown> | null } }).error
        : undefined;
    throw new ApiError(
      res.status,
      err?.code ?? `HTTP_${res.status}`,
      err?.message ?? (text || res.statusText),
      err?.details ?? null,
    );
  }
  return body as T;
}

function qs(params: Record<string, unknown | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ---------- stages ----------

export const stagesApi = {
  list: (params: { page?: number; page_size?: number; is_active?: boolean } = {}) =>
    request<Page<StageRead>>(`/stages${qs(params)}`),
  get: (id: number) => request<StageRead>(`/stages/${id}`),
  detailed: (id: number) => request<StageReadDetailed>(`/stages/${id}/detailed`),
  operations: (id: number) => request<OperationRead[]>(`/stages/${id}/operations`),
};

// ---------- orders ----------

export const ordersApi = {
  list: (
    params: {
      page?: number;
      page_size?: number;
      stage_id?: number;
      status?: OrderStatus;
      code?: string;
    } = {},
  ) => request<Page<ProductionOrderRead>>(`/orders${qs(params)}`),
  get: (id: UUID) => request<ProductionOrderRead>(`/orders/${id}`),
  detailed: (id: UUID) =>
    request<ProductionOrderReadDetailed>(`/orders/${id}/detailed`),
  create: (data: ProductionOrderCreate) =>
    request<ProductionOrderRead>("/orders", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: UUID, data: ProductionOrderUpdate) =>
    request<ProductionOrderRead>(`/orders/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  adjust: (id: UUID, data: OrderAdjustmentCreate) =>
    request<OrderAdjustmentRead>(`/orders/${id}/adjust`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  adjustments: (id: UUID) =>
    request<OrderAdjustmentRead[]>(`/orders/${id}/adjustments`),
};

// ---------- manufacturing orders (заказ на производство) ----------
//
// Заголовочный документ над production_orders. Base /manufacturing-orders.
// Права: read — ADMIN+SHIFT_LEAD; create/update — ADMIN; launch — ADMIN+SHIFT_LEAD.

export const manufacturingOrdersApi = {
  list: (
    params: {
      page?: number;
      page_size?: number;
      status?: ManufacturingOrderStatus;
      article?: string;
      source_system?: ManufacturingOrderSource;
      process_key?: string;
    } = {},
  ) =>
    request<Page<ManufacturingOrderRead>>(`/manufacturing-orders${qs(params)}`),
  get: (id: UUID) =>
    request<ManufacturingOrderDetailed>(`/manufacturing-orders/${id}`),
  create: (data: ManufacturingOrderCreate) =>
    request<ManufacturingOrderRead>("/manufacturing-orders", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: UUID, data: ManufacturingOrderUpdate) =>
    request<ManufacturingOrderRead>(`/manufacturing-orders/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  /** Техпроцессы, по которым заводятся заказы, с описанием заказа. */
  processes: () => request<OrderProcess[]>("/manufacturing-orders/processes"),
  /** Предпросмотр расчётных атрибутов (без сохранения). */
  preview: (data: ManufacturingOrderPreviewRequest) =>
    request<ManufacturingOrderPreviewResponse>(
      "/manufacturing-orders/preview",
      { method: "POST", body: JSON.stringify(data) },
    ),
  launch: (id: UUID) =>
    request<ManufacturingOrderLaunchResponse>(
      `/manufacturing-orders/${id}/launch`,
      { method: "POST" },
    ),
  /** Приёмка партии — создаёт изделия (material_items) по внешним
   *  серийникам. Требует статус IN_PRODUCTION. Идемпотентно. */
  receive: (id: UUID) =>
    request<ManufacturingOrderReceiveResponse>(
      `/manufacturing-orders/${id}/receive`,
      { method: "POST" },
    ),
  /** Пакетная печать: паспорт на каждое принятое изделие + упаковочный лист.
   *  Паспорта требуют, чтобы партия была принята (см. receive). */
  printBatch: (id: UUID, params: ManufacturingOrderPrintBatchRequest = {}) =>
    request<ManufacturingOrderPrintBatchResponse>(
      `/manufacturing-orders/${id}/print-batch`,
      { method: "POST", body: JSON.stringify(params) },
    ),
  /** Удалить заказ. Только админ (manufacturing_order.delete); 409, если по
   *  заказу уже зафиксировано производство. */
  remove: (id: UUID) =>
    request<void>(`/manufacturing-orders/${id}`, { method: "DELETE" }),

  // ── короткий путь: стадии отмечаются «готово» в один клик ──
  /** Начальник производства берёт заказ в работу: DRAFT→READY→launch. */
  takeIntoWork: (id: UUID) =>
    request<ManufacturingOrderLaunchResponse>(
      `/manufacturing-orders/${id}/take-into-work`,
      { method: "POST" },
    ),
  /** Чеклист стадий короткого пути со статусами. */
  stages: (id: UUID) =>
    request<ShortFlowListResponse>(`/manufacturing-orders/${id}/stages`),
  /** Отметить стадию короткого пути готовой (one-click, последовательно). */
  advanceStage: (id: UUID, stageCode: string) =>
    request<ShortFlowAdvanceResponse>(
      `/manufacturing-orders/${id}/stages/${encodeURIComponent(stageCode)}/advance`,
      { method: "POST" },
    ),
};

// ---------- flows (material items) ----------

export const flowsApi = {
  list: (
    params: {
      page?: number;
      page_size?: number;
      stage_id?: number;
      status?: FlowStatus;
      code?: string;
      flow_type?: FlowType;
      batch_id?: UUID;
    } = {},
  ) => request<Page<MaterialItemRead>>(`/flows${qs(params)}`),
  get: (id: UUID) => request<MaterialItemRead>(`/flows/${id}`),
  create: (data: MaterialItemCreate) =>
    request<MaterialItemRead>("/flows", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: UUID, data: MaterialItemUpdate) =>
    request<MaterialItemRead>(`/flows/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  trace: (id: UUID) => request<MaterialItemTrace>(`/flows/${id}/trace`),
  lookup: (scan: MaterialItemScan) =>
    request<MaterialItemRead>("/flows/lookup", {
      method: "POST",
      body: JSON.stringify(scan),
    }),
};

// ---------- operations / executions ----------

export const operationsApi = {
  start: (operationId: UUID, data: ExecutionStart) =>
    request<ExecutionRead>(`/operations/${operationId}/start`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

export const executionsApi = {
  list: (
    params: {
      page?: number;
      page_size?: number;
      operation_id?: UUID;
      order_id?: UUID;
      status?: ExecutionStatus;
      operator_id?: UUID;
      station_id?: string;
    } = {},
  ) => request<Page<ExecutionRead>>(`/executions${qs(params)}`),
  get: (id: UUID) => request<ExecutionRead>(`/executions/${id}`),
  complete: (id: UUID, data: ExecutionComplete = {}) =>
    request<ExecutionRead>(`/executions/${id}/complete`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  qc: (id: UUID, data: ExecutionQC) =>
    request<ExecutionRead>(`/executions/${id}/qc`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  scan: (id: UUID, data: ScanEventInput) =>
    request<ScanEventRead>(`/executions/${id}/scan`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  scans: (id: UUID) => request<ScanEventRead[]>(`/executions/${id}/scans`),
};

// ---------- devices ----------

export const devicesApi = {
  list: (
    params: {
      page?: number;
      page_size?: number;
      type?: string;
      status?: string;
      station_id?: string;
      q?: string;
    } = {},
  ) => request<Page<DeviceRead>>(`/devices${qs(params)}`),
  get: (id: UUID) => request<DeviceRead>(`/devices/${id}`),
};

// ---------- passports (PD_9) ----------

export interface ProductPassportRead {
  id: UUID;
  item_id: UUID;
  test_data: Record<string, unknown>;
  performance_metrics: Record<string, unknown> | null;
  rfid_tag: string | null;
  created_at: ISODateTime;
}

export interface ProductPassportCreate {
  item_id: UUID;
  test_data: Record<string, unknown>;
  performance_metrics?: Record<string, unknown> | null;
  rfid_tag?: string | null;
}

export const passportsApi = {
  /** Backend exposes this under flows, not under a standalone /passports
   *  collection — keeping the frontend API surface stable while routing
   *  to the real endpoint. */
  byItem: (itemId: UUID) =>
    request<ProductPassportRead>(`/flows/${itemId}/passport`),
  /** Real endpoint is POST /flows/{id}/issue-passport with a different
   *  body shape ({test_data_override, rfid_tag, skip_auto_print}).
   *  Caller passes ProductPassportCreate.test_data → mapped to
   *  test_data_override; .rfid_tag → rfid_tag.  Response is wrapped
   *  ({passport, source, print_job_id}); we unwrap to keep the shape
   *  consumers already expect. */
  create: async (data: ProductPassportCreate): Promise<ProductPassportRead> => {
    const resp = await request<{
      passport: ProductPassportRead;
      source: string;
      print_job_id: string | null;
    }>(`/flows/${data.item_id}/issue-passport`, {
      method: "POST",
      body: JSON.stringify({
        test_data_override: data.test_data,
        ...(data.rfid_tag !== undefined ? { rfid_tag: data.rfid_tag } : {}),
        skip_auto_print: false,
      }),
    });
    return resp.passport;
  },
  /** Re-print an already-issued passport (W5). Enqueues a passport print
   *  job via POST /flows/{id}/print-passport (waterfall override→measured→
   *  target). Async: returns the enqueued job id. */
  reprint: (itemId: UUID, total = 1) =>
    request<{ job_id: string; status: string; metrics: unknown }>(
      `/flows/${itemId}/print-passport`,
      { method: "POST", body: JSON.stringify({ total }) },
    ),
};

// ---------- print ----------

/** Sentinel agent id for the W8 "save to PDF, don't print" pseudo-agent.
 *  Sending this as `agent_id` renders the PDF server-side and suppresses ALL
 *  print dispatch — the job is downloadable from history but never printed.
 *  Kept in sync with backend PDF_SINK_AGENT_ID (services/print_gateway.ts). */
export const PDF_SINK_AGENT_ID = "pdf-sink";

/** Единый ярлык опции «не печатать — сохранить в PDF».  Используется во ВСЕХ
 *  диалогах печати (главная печать, QR станций/рулонов), чтобы опция была
 *  одинаковой и не терялась при добавлении новых мест печати. */
export const PDF_SINK_LABEL = "Не печатать — сохранить в PDF";

export interface PrintTemplateUpdate {
  name?: string;
  description?: string | null;
  brand?: string;
  page_width_mm?: number;
  page_height_mm?: number;
  /** Default print target.  Pass null to clear. */
  default_agent_id?: string | null;
  default_printer_name?: string | null;
  /** Роли доступа к шаблону. Пустой массив/null → только админ. */
  allowed_roles?: string[] | null;
}

export interface PrintTemplateRead {
  id: string;
  kind: string;
  brand: string;
  name: string;
  description: string | null;
  version: number;
  page_width_mm: number;
  page_height_mm: number;
  /** Path under PRINT_STORAGE_ROOT.  Empty string when no SVG is
   *  uploaded — either a freshly created template, or the operator
   *  detached the background via DELETE /admin/templates/:id/background.
   *  All consumers use truthy-checks (`if (path)`), so '' is treated
   *  the same as absent. */
  background_svg_path: string;
  /** Path to generated Typst source.  Empty string until placeholders
   *  are saved at least once via PUT /placeholders. */
  typst_source_path: string;
  /** Plugin-format: { variables: [{ key, type, label, required, ... }] }
   *  See backend-ts/src/print/db/schema/template.ts. */
  variables_schema: Record<string, unknown>;
  /** Renderer-side layout (x/y/width/height/font).  Array of placeholder
   *  objects when saved; backend may still serialise as a record. */
  placeholders: Record<string, unknown> | unknown[];
  is_archived: boolean;
  preview_png_path: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  /** Admin-set default print target.  EnqueueModal pre-selects these
   *  (operator can still override).  null = no default configured. */
  default_agent_id: string | null;
  default_printer_name: string | null;
  /** Роли, которым шаблон доступен для печати+редактирования. null/[] =
   *  только админ + держатели print.template.manage. */
  allowed_roles: string[] | null;
}

export interface PrintJobCreate {
  kind: string;
  params: Record<string, unknown>;
  total?: number;
  template_id?: string;
  production_task_ref?: string;
  /** Pre-assign the printer within the chosen agent. */
  printer_name?: string;
  /** Pre-assign the job to a specific agent.  Omit to let any
   *  capable online agent claim it. */
  agent_id?: string;
}

export interface PrintAgentCapable {
  id: string;
  name: string;
  last_seen_at: string | null;
  printers: Array<{ id: string; name: string; windows_name: string | null }>;
}

export interface PrintJobListItem {
  job_id: string;
  status: string;
  kind: string;
  template_name: string;
  total: number;
  rendered: number;
  has_pdf: boolean;
  agent_id: string | null;
  agent_name: string | null;
  printer_name: string | null;
  printed_at: string | null;
  created_at: string;
  finished_at: string | null;
  error: string | null;
}

export interface PrintJobRead {
  job_id: string;
  status: "QUEUED" | "RUNNING" | "DONE" | "FAILED";
  kind?: string;
  total?: number;
  rendered?: number;
  error?: string | null;
  production_task_ref?: string | null;
  created_at?: ISODateTime;
  started_at?: ISODateTime | null;
  finished_at?: ISODateTime | null;
}

// ---------- print: admin template editor ----------

export interface PrintTemplateCreate {
  kind: string;
  brand: string;
  name: string;
  description?: string;
  page_width_mm: number;
  page_height_mm: number;
  allowed_roles?: string[];
}

export interface PrintTemplateUpdate {
  name?: string;
  description?: string | null;
  brand?: string;
  page_width_mm?: number;
  page_height_mm?: number;
}

/** Placeholder layout shape consumed by the renderer.  Mirrors
 *  packages/print-plugin/packages/shared placeholderSchema — kept loose
 *  here because the admin editor mostly passes blobs through.  All
 *  coordinates are in millimetres. */
export interface PrintTemplatePlaceholder {
  id: string;
  key: string;
  x: number;
  y: number;
  width: number;
  height: number;
  font?: string;
  fontSize?: number;
  fontWeight?: number;
  align?: "left" | "center" | "right";
  color?: string;
  /** Статическая подпись перед значением (напр. "Поток Min –"). */
  prefix?: string;
  /** Статическая единица измерения, печатается после значения (напр. "%"). */
  suffix?: string;
  /** Не печатать блок целиком, когда значение пустое — вместе с подписью и
   *  единицей.  Ноль пустотой не считается. */
  hideWhenEmpty?: boolean;
  /** Источник значения при печати по изделию/заказу: order.article,
   *  item.serial, attr.<атрибут>, metric.<показатель>. */
  source?: string;
  [k: string]: unknown;
}

/** Variables schema sent on PUT /placeholders.  Matches what the
 *  print plugin expects: `{ variables: [{ key, label, type, ...}] }`. */
export interface PrintTemplateVariablesSchema {
  variables: Array<{
    key: string;
    label: string;
    type: "text" | "number" | "date" | "enum" | "auto_increment";
    required?: boolean;
    default?: string | number;
    min?: number;
    max?: number;
    maxLength?: number;
    enumOptions?: string[];
    prefix?: string;
    padWidth?: number;
    startAt?: number;
    editableByOperator?: boolean;
    [k: string]: unknown;
  }>;
}

export interface PrintTemplateExtractDraft {
  placeholders: PrintTemplatePlaceholder[];
  variables: PrintTemplateVariablesSchema["variables"];
}

export const printApi = {
  label: (data: PrintLabelRequest) =>
    request<PrintLabelResponse>("/print/labels", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  listTemplates: (params: { kind?: string; brand?: string; include_archived?: boolean } = {}) =>
    request<{ items: PrintTemplateRead[] }>(`/print/templates${qs(params)}`),
  getTemplate: (id: string) => request<PrintTemplateRead>(`/print/templates/${id}`),
  enqueueJob: (data: PrintJobCreate) =>
    request<PrintJobRead>("/print/jobs", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  getJob: (id: string) => request<PrintJobRead>(`/print/jobs/${id}`),
  /** Recent print jobs: история для оператора, со ссылками на PDF и
   *  именами агента/принтера если job уже claimed. */
  listJobs: (params: { limit?: number; status?: string } = {}) =>
    request<{ items: PrintJobListItem[] }>(`/print/jobs${qs(params)}`),
  /** Hard-delete a job (cancels in-flight render if any + removes PDF). */
  deleteJob: (id: string) =>
    request<void>(`/print/jobs/${id}`, { method: "DELETE" }),
  /** Hard-delete several jobs in one call (history cleanup). Each id is
   *  processed independently — response lists deleted vs failed. */
  deleteJobsBulk: (jobIds: string[]) =>
    request<{ deleted: string[]; failed: { job_id: string; error: string }[] }>(
      `/print/jobs/bulk-delete`,
      { method: "POST", body: JSON.stringify({ job_ids: jobIds }) },
    ),
  /** Clone job's template/params/total and queue a new one — optionally
   *  pre-assigned to a specific agent + printer. */
  reprintJob: (
    id: string,
    target: { agent_id?: string; printer_name?: string } = {},
  ) =>
    request<PrintJobRead>(`/print/jobs/${id}/reprint`, {
      method: "POST",
      body: JSON.stringify(target),
    }),
  /** List print-agents currently online and able to render `kind`.
   *  Used by autodispatch / template-default suggestions where
   *  handles_kinds is a real routing constraint. */
  listCapableAgents: (kind: string) =>
    request<{ items: PrintAgentCapable[] }>(
      `/print/agents/capable${qs({ kind })}`,
    ),
  /** All currently-online print-agents (no kind filter).  Used by the
   *  operator's manual-print UI — kind of a template isn't a real
   *  routing constraint, just a label.  Operator picks both agent
   *  and printer themselves. */
  listOnlineAgents: () =>
    request<{ items: PrintAgentCapable[] }>(`/print/agents/online`),
  /** Fetch the rendered PDF as a Blob, attaching the Bearer token.
   *  We can't use `<a href={…}>` because the browser does NOT send
   *  Authorization headers from anchor clicks → the request would 401.
   *  Caller wraps the blob with URL.createObjectURL for download or
   *  inline preview. */
  fetchJobPdf: async (id: string): Promise<Blob> => {
    const res = await fetch(`${BASE}/print/jobs/${id}/pdf`, {
      headers: { ...authHeader() },
    });
    if (res.status === 401) window.dispatchEvent(new Event("mes:auth-expired"));
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let err: { code?: string; message?: string } | undefined;
      try {
        const body = text ? JSON.parse(text) : null;
        err = body && typeof body === "object" && "error" in body
          ? (body as { error?: { code?: string; message?: string } }).error
          : undefined;
      } catch {
        /* non-JSON */
      }
      throw new ApiError(
        res.status,
        err?.code ?? `HTTP_${res.status}`,
        err?.message ?? (text || res.statusText),
        null,
      );
    }
    return res.blob();
  },
  /** Absolute URL to GET the raw background SVG.  Admin editor embeds
   *  this in an `<img>` / `<object>` to show the canvas underlay. */
  templateBackgroundUrl: (id: string) => `${BASE}/print/templates/${id}/background`,
};

export const printAdminApi = {
  create: (data: PrintTemplateCreate) =>
    request<PrintTemplateRead>("/print/admin/templates", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: string, data: PrintTemplateUpdate) =>
    request<PrintTemplateRead>(`/print/admin/templates/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  archive: (id: string) =>
    request<void>(`/print/admin/templates/${id}/archive`, { method: "POST" }),
  restore: (id: string) =>
    request<void>(`/print/admin/templates/${id}/restore`, { method: "POST" }),
  remove: (id: string) =>
    request<void>(`/print/admin/templates/${id}`, { method: "DELETE" }),
  /** Raw-body upload — the backend reads `await c.req.text()`, expecting
   *  the SVG source as the whole request body with Content-Type
   *  image/svg+xml.  We bypass the JSON request() helper so no
   *  multipart wrapping is added. */
  uploadBackground: async (id: string, file: File) => {
    const svgText = await file.text();
    const res = await fetch(`${BASE}/print/admin/templates/${id}/background`, {
      method: "POST",
      headers: {
        ...authHeader(),
        "content-type": "image/svg+xml",
      },
      body: svgText,
    });
    if (res.status === 401) window.dispatchEvent(new Event("mes:auth-expired"));
    const text = await res.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
    }
    if (!res.ok) {
      const err =
        body && typeof body === "object" && "error" in body
          ? (body as { error?: { code?: string; message?: string; details?: Record<string, unknown> | null } }).error
          : undefined;
      throw new ApiError(
        res.status,
        err?.code ?? `HTTP_${res.status}`,
        err?.message ?? (text || res.statusText),
        err?.details ?? null,
      );
    }
    return body as { path: string; sha256: string; auto_extracted: number };
  },
  removeBackground: (id: string) =>
    request<void>(`/print/admin/templates/${id}/background`, { method: "DELETE" }),
  extractPlaceholders: (id: string) =>
    request<PrintTemplateExtractDraft>(
      `/print/admin/templates/${id}/extract-placeholders`,
      { method: "POST" },
    ),
  updatePlaceholders: (
    id: string,
    data: {
      placeholders: PrintTemplatePlaceholder[];
      variables_schema: PrintTemplateVariablesSchema;
    },
  ) =>
    request<{ typst_source_path: string }>(`/print/admin/templates/${id}/placeholders`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  clone: (id: string, name?: string) =>
    request<{ id: string }>(`/print/admin/templates/${id}/clone`, {
      method: "POST",
      body: JSON.stringify(name ? { name } : {}),
    }),
  rotate: (id: string) =>
    request<void>(`/print/admin/templates/${id}/rotate`, { method: "POST" }),
  orient: (id: string, action: "swap" | "rotate-cw" | "rotate-ccw") =>
    request<void>(`/print/admin/templates/${id}/orient`, {
      method: "POST",
      body: JSON.stringify({ action }),
    }),
  /** Fetch the raw SVG background as a Blob.  Browsers don't attach
   *  our Bearer header to <img> / <object> / SVG <image>, so we tunnel
   *  through fetch() and let the caller wrap into URL.createObjectURL. */
  fetchBackground: async (id: string): Promise<Blob> => {
    const res = await fetch(`${BASE}/print/templates/${id}/background`, {
      headers: { ...authHeader() },
    });
    if (res.status === 401) window.dispatchEvent(new Event("mes:auth-expired"));
    if (!res.ok) {
      throw new ApiError(
        res.status,
        `HTTP_${res.status}`,
        `failed to load background: ${res.statusText}`,
        null,
      );
    }
    return res.blob();
  },
  /** Render a one-shot preview PDF with mock values.  Returns the
   *  binary blob — caller can wrap in `URL.createObjectURL` for an
   *  `<iframe>` / `<embed>` preview. */
  preview: async (
    id: string,
    values: Record<string, string | number>,
    index = 0,
  ): Promise<Blob> => {
    const res = await fetch(`${BASE}/print/admin/templates/${id}/preview`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify({ values, index }),
    });
    if (res.status === 401) window.dispatchEvent(new Event("mes:auth-expired"));
    if (!res.ok) {
      const text = await res.text();
      const body = text ? JSON.parse(text) : null;
      const err = body?.error;
      throw new ApiError(
        res.status,
        err?.code ?? `HTTP_${res.status}`,
        err?.message ?? res.statusText,
        err?.details ?? null,
      );
    }
    return res.blob();
  },
};

// ---------- admin: print-agents ----------

export interface PrintAgentPrinter {
  /** Server-assigned uuid; absent when the row is being authored client-side. */
  id?: string;
  /** Logical name picked by the admin — e.g. "laser-bw", "label-sticker". */
  name: string;
  /** Exact Windows printer name as it appears in Print Management.
   *  Optional / nullable: admin doesn't know it at create-time, the
   *  agent fills it in via POST /register from its TOML config. */
  windows_name?: string | null;
  /** Template kind slugs this printer can render. */
  handles_kinds: string[];
  is_active?: boolean;
}

export interface PrintAgentRead {
  id: string;
  name: string;
  is_active: boolean;
  /** ISO timestamp of last SSE connect / heartbeat; null if the agent
   *  has never connected. */
  last_seen_at: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  printers: PrintAgentPrinter[];
}

export interface PrintAgentCreateRequest {
  name: string;
  printers?: PrintAgentPrinter[];
}

/** Returned by POST /admin/print-agents and POST .../rotate-token — the
 *  raw bearer string ONLY appears here.  After this response, the DB
 *  has only sha256(token) and the value cannot be read back. */
export interface PrintAgentCreateResponse extends PrintAgentRead {
  token: string;
}

export const printAgentsAdminApi = {
  list: () =>
    request<{ items: PrintAgentRead[] }>("/admin/print-agents"),
  get: (id: string) => request<PrintAgentRead>(`/admin/print-agents/${id}`),
  create: (data: PrintAgentCreateRequest) =>
    request<PrintAgentCreateResponse>("/admin/print-agents", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: string, data: { name?: string; is_active?: boolean }) =>
    request<PrintAgentRead>(`/admin/print-agents/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  remove: (id: string) =>
    request<void>(`/admin/print-agents/${id}`, { method: "DELETE" }),
  rotateToken: (id: string) =>
    request<{ agent_id: string; token: string }>(
      `/admin/print-agents/${id}/rotate-token`,
      { method: "POST" },
    ),
  replacePrinters: (id: string, printers: PrintAgentPrinter[]) =>
    request<PrintAgentRead>(`/admin/print-agents/${id}/printers`, {
      method: "PUT",
      body: JSON.stringify({ printers }),
    }),
};

// ---------- admin: users ----------

export const adminUsersApi = {
  list: (
    params: {
      page?: number;
      page_size?: number;
      role?: OperatorRole;
      is_active?: boolean;
      q?: string;
    } = {},
  ) => request<Page<AdminUserRead>>(`/admin/users${qs(params)}`),
  get: (id: UUID) => request<AdminUserRead>(`/admin/users/${id}`),
  create: (data: AdminUserCreate) =>
    request<AdminUserRead>("/admin/users", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: UUID, data: AdminUserUpdate) =>
    request<AdminUserRead>(`/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  resetPassword: (id: UUID, data: AdminResetPassword) =>
    request<AdminUserRead>(`/admin/users/${id}/reset-password`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  resetPin: (id: UUID, data: AdminResetPin) =>
    request<AdminUserRead>(`/admin/users/${id}/reset-pin`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  rotateNfc: (id: UUID, data: AdminRotateNfc) =>
    request<AdminUserRead>(`/admin/users/${id}/rotate-nfc`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  issueQrBadge: (
    id: UUID,
    data: { rotate?: boolean; agent_id?: string; printer_name?: string } = {},
  ) =>
    request<{
      qr_value: string;
      tag_id: UUID;
      revoked_count: number;
      print_job_id: string;
      user: AdminUserRead;
    }>(`/admin/users/${id}/qr-badge`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  activate: (id: UUID) =>
    request<AdminUserRead>(`/admin/users/${id}/activate`, { method: "POST" }),
  deactivate: (id: UUID) =>
    request<AdminUserRead>(`/admin/users/${id}/deactivate`, { method: "POST" }),
  setStages: (id: UUID, data: AdminAssignStages) =>
    request<AdminUserRead>(`/admin/users/${id}/assignments/stages`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  setDevices: (id: UUID, data: AdminAssignDevices) =>
    request<AdminUserRead>(`/admin/users/${id}/assignments/devices`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  setStations: (id: UUID, data: AdminAssignStations) =>
    request<AdminUserRead>(`/admin/users/${id}/assignments/stations`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  listStations: (id: UUID) =>
    request<StationRead[]>(`/admin/users/${id}/stations`),
};

// ---------- admin: roles ----------

export const adminRolesApi = {
  listPermissions: () => request<AdminPermissionRead[]>("/admin/permissions"),
  listRoles: () => request<AdminRoleRead[]>("/admin/roles"),
  getRole: (role: OperatorRole) => request<AdminRoleRead>(`/admin/roles/${role}`),
  setRolePermissions: (role: OperatorRole, data: AdminRolePermissionsUpdate) =>
    request<AdminRoleRead>(`/admin/roles/${role}/permissions`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  createRole: (data: AdminRoleCreate) =>
    request<AdminRoleRead>("/admin/roles", { method: "POST", body: JSON.stringify(data) }),
  deleteRole: (role: OperatorRole) =>
    request<void>(`/admin/roles/${role}`, { method: "DELETE" }),
};

// ---------- admin: stations ----------

export const adminStationsApi = {
  list: (
    params: {
      page?: number;
      page_size?: number;
      stage_id?: number;
      is_active?: boolean;
      q?: string;
    } = {},
  ) => request<Page<StationRead>>(`/admin/stations${qs(params)}`),
  get: (id: number) => request<StationReadDetailed>(`/admin/stations/${id}`),
  create: (data: StationCreate) =>
    request<StationRead>("/admin/stations", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: number, data: StationUpdate) =>
    request<StationRead>(`/admin/stations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  activate: (id: number) =>
    request<StationRead>(`/admin/stations/${id}/activate`, { method: "POST" }),
  deactivate: (id: number) =>
    request<StationRead>(`/admin/stations/${id}/deactivate`, { method: "POST" }),
  setOperators: (id: number, data: StationAssignOperators) =>
    request<StationReadDetailed>(`/admin/stations/${id}/operators`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  printQr: (
    id: number,
    body: { agent_id?: string; printer_name?: string } = {},
  ) =>
    request<{ job_id: string; status: string }>(
      `/admin/stations/${id}/print-qr`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),
};

// ---------- stations (any authenticated user) ----------

export const stationsApi = {
  list: (params: { mine?: boolean; include_inactive?: boolean } = {}) =>
    request<StationRead[]>(`/stations${qs(params)}`),
};

// ---------- admin: audit log ----------

export const adminAuditApi = {
  list: (
    params: {
      page?: number;
      page_size?: number;
      entity_type?: string;
      action?: string;
      actor_id?: UUID;
      entity_id?: UUID;
      since?: string;
      until?: string;
    } = {},
  ) => request<Page<AuditEntry>>(`/admin/audit${qs(params)}`),
  facets: () => request<AuditFacets>("/admin/audit/facets"),
};

// ---------- admin: production cycle editor ----------

export const adminCycleApi = {
  patchStage: (id: number, patch: StageConfigPatch) =>
    request<StageRead>(`/admin/cycle/stages/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  patchOperation: (id: UUID, patch: OperationConfigPatch) =>
    request<OperationRead>(`/admin/cycle/operations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // --- constructor: declarative graph + create / delete (S2.2) ---
  getGraph: () =>
    request<{ stages: ProcessStageGraph[] }>("/admin/cycle"),

  createStage: (body: CreateStageBody) =>
    request<StageCreated>("/admin/cycle/stages", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteStage: (id: number) =>
    request<void>(`/admin/cycle/stages/${id}`, { method: "DELETE" }),

  createOperation: (body: CreateOperationBody) =>
    request<OperationCreated>("/admin/cycle/operations", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteOperation: (id: string) =>
    request<void>(`/admin/cycle/operations/${id}`, { method: "DELETE" }),

  createFlow: (body: CreateFlowBody) =>
    request<FlowCreated>("/admin/cycle/flows", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteFlow: (id: string) =>
    request<void>(`/admin/cycle/flows/${id}`, { method: "DELETE" }),

  // --- i18n S4.3: per-locale name/label editing ---
  // Upserts (key, locale, text) rows straight into the translations
  // catalogue.  The cycle graph (getGraph) already returns each entity's
  // `name_key` + `names` map for the editor to populate these.
  upsertTranslations: (
    entries: Array<{ key: string; locale: string; text: string }>,
  ) =>
    request<{ upserted: number }>("/admin/cycle/translations", {
      method: "POST",
      body: JSON.stringify({ entries }),
    }),
};

// ---------- i18n: locale discovery ----------

export const i18nApi = {
  /** Supported content locales (catalogue ∪ built-in defaults) + default. */
  locales: () =>
    request<{ locales: string[]; default: string }>("/i18n/locales"),
};

// ---------- admin: metric definitions (метрики изделия) ----------

export const adminMetricsApi = {
  list: (scope?: string) =>
    request<{ items: MetricDefinition[] }>(
      "/admin/metrics" + (scope ? `?scope=${scope}` : ""),
    ),
  create: (body: CreateMetricBody) =>
    request<MetricDefinition>("/admin/metrics", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: string, patch: UpdateMetricBody) =>
    request<MetricDefinition>(`/admin/metrics/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  remove: (id: string) =>
    request<void>(`/admin/metrics/${id}`, { method: "DELETE" }),
};

// ---------- admin: ERP integration outbox ----------

export const adminIntegrationsApi = {
  listOutbox: (
    params: {
      status?: IntegrationOutboxStatus;
      page?: number;
      page_size?: number;
    } = {},
  ) =>
    request<Page<IntegrationOutboxRow>>(
      `/admin/integrations/outbox${qs(params)}`,
    ),
  stats: () =>
    request<IntegrationOutboxStats>(`/admin/integrations/outbox/stats`),
  requeue: (id: UUID) =>
    request<void>(`/admin/integrations/outbox/${id}/requeue`, {
      method: "POST",
    }),
  // STAITER pull-адаптер (приём заказов из STAITER).
  pullSettings: () =>
    request<ErpPullSettings>(`/admin/integrations/pull/settings`),
  updatePullSettings: (patch: ErpPullSettingsPatch) =>
    request<ErpPullSettings>(`/admin/integrations/pull/settings`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  runPullNow: () =>
    request<ErpPullCycleResult>(`/admin/integrations/pull/run-now`, {
      method: "POST",
    }),
};

// ---------- admin: station-assignment approvals (Block 9D) ----------

export const adminStationAssignmentsApi = {
  listPending: () =>
    request<StationAssignmentReadDetailed[]>("/admin/station-assignments"),
  listActive: () =>
    request<StationAssignmentReadDetailed[]>("/admin/station-assignments/active"),
  approve: (operatorId: UUID, stationId: number) =>
    request<StationAssignmentRead>(
      `/admin/station-assignments/${operatorId}/${stationId}/approve`,
      { method: "POST" },
    ),
  reject: (operatorId: UUID, stationId: number) =>
    request<StationAssignmentRead>(
      `/admin/station-assignments/${operatorId}/${stationId}/reject`,
      { method: "POST" },
    ),
  revoke: (operatorId: UUID, stationId: number) =>
    request<StationAssignmentRead>(
      `/admin/station-assignments/${operatorId}/${stationId}/revoke`,
      { method: "POST" },
    ),
};

// ---------- admin: tags ----------

export const adminTagsApi = {
  list: (
    params: {
      page?: number;
      page_size?: number;
      target_type?: TagTargetType;
      target_id?: string;
      kind?: TagKind;
      is_active?: boolean;
      q?: string;
    } = {},
  ) => request<Page<TagRead>>(`/admin/tags${qs(params)}`),
  create: (data: TagCreate) =>
    request<TagRead>("/admin/tags", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  unbind: (id: UUID) =>
    request<TagRead>(`/admin/tags/${id}`, { method: "DELETE" }),
  // Перепечать QR этой метки (operator-badge с тем же токеном).
  printTag: (
    id: UUID,
    body: { agent_id?: string; printer_name?: string } = {},
  ) =>
    request<{ job_id: string; status: string }>(`/admin/tags/${id}/print`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // nested read-only lists per entity
  forUser: (userId: UUID) =>
    request<TagRead[]>(`/admin/users/${userId}/tags`),
  forStation: (stationId: number | string) =>
    request<TagRead[]>(`/admin/stations/${stationId}/tags`),
  forDevice: (deviceId: UUID) =>
    request<TagRead[]>(`/admin/devices/${deviceId}/tags`),
  forOrder: (orderId: UUID) =>
    request<TagRead[]>(`/admin/orders/${orderId}/tags`),
  forFlow: (flowId: UUID) =>
    request<TagRead[]>(`/admin/flows/${flowId}/tags`),
};

// ---------- scan: universal lookup ----------

export const scanApi = {
  lookup: (data: ScanLookupRequest) =>
    request<ScanLookupResponse>("/scan/lookup", {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// ---------- auth: change-password ----------

export const authApi = {
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    }),
  logout: (refreshToken: string) =>
    request<void>("/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),
};

// ---------- admin: inventory (material rolls + batches) ----------

export const adminInventoryApi = {
  listMaterials: (params: { active?: boolean; q?: string } = {}) =>
    request<{ items: InventoryMaterial[] }>(`/admin/inventory/materials${qs(params)}`),
  createMaterial: (data: InventoryMaterialCreate) =>
    request<InventoryMaterial>("/admin/inventory/materials", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  patchMaterial: (id: UUID, patch: InventoryMaterialPatch) =>
    request<InventoryMaterial>(`/admin/inventory/materials/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  listBatches: (params: { material_id?: UUID; qc_status?: string } = {}) =>
    request<{ items: InventoryBatch[] }>(`/admin/inventory/batches${qs(params)}`),
  createBatch: (data: InventoryBatchCreate) =>
    request<InventoryBatch>("/admin/inventory/batches", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listRolls: (
    params: {
      material_id?: UUID;
      batch_id?: UUID;
      station_id?: number;
      status?: string;
      q?: string;
    } = {},
  ) => request<{ items: InventoryRoll[] }>(`/admin/inventory/rolls${qs(params)}`),
  createRoll: (data: InventoryRollCreate) =>
    request<InventoryRoll>("/admin/inventory/rolls", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  loadRoll: (id: UUID, station_id: number, note?: string | null) =>
    request<InventoryRoll>(`/admin/inventory/rolls/${id}/load`, {
      method: "POST",
      body: JSON.stringify({ station_id, note }),
    }),
  unloadRoll: (id: UUID, note?: string | null) =>
    request<InventoryRoll>(`/admin/inventory/rolls/${id}/unload`, {
      method: "POST",
      body: JSON.stringify({ note }),
    }),
  printRollQr: (
    id: UUID,
    body: { agent_id?: string; printer_name?: string } = {},
  ) =>
    request<{ job_id: string; status: string }>(
      `/admin/inventory/rolls/${id}/print-qr`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),

  consume: (data: {
    roll_id?: UUID | null;
    batch_id?: UUID | null;
    qty: number;
    production_order_id?: UUID | null;
    note?: string | null;
  }) =>
    request<InventoryConsumptionEvent>(`/admin/inventory/consume`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  listConsumption: (
    params: { material_id?: UUID; production_order_id?: UUID; limit?: number } = {},
  ) =>
    request<{ items: InventoryConsumptionEvent[] }>(
      `/admin/inventory/consumption${qs(params)}`,
    ),
  loadedOnStation: (stationId: number) =>
    request<{
      items: Array<{
        roll_id: UUID;
        roll_code: string;
        material_id: UUID;
        material_code: string;
        remaining_qty: number;
        stock_uom: string;
      }>;
    }>(`/admin/inventory/stations/${stationId}/loaded`),
};

// ---------- auth: personal API tokens (MCP agents) ----------
//
// Bearer tokens issued to a human operator so they can point an MCP
// client (Claude Code etc.) at <адрес установки>/api/v1/mcp. Raw value
// (`mespat_…`) is only ever returned once, on create — same "show once"
// contract as print-agent tokens (see PrintAgentCreateResponse above).

export interface ApiTokenRead {
  id: UUID;
  name: string;
  created_at: ISODateTime;
  expires_at: ISODateTime | null;
  revoked_at: ISODateTime | null;
  last_used_at: ISODateTime | null;
}

/** Admin view (GET /auth/api-tokens/all) — same shape + owner info. */
export interface ApiTokenAdminRead extends ApiTokenRead {
  operator_id: UUID;
  operator_name: string;
  operator_role: OperatorRole;
}

export interface ApiTokenCreateResponse {
  /** Raw `mespat_…` secret — shown once, never retrievable again. */
  token: string;
  api_token: ApiTokenRead;
}

/** POST /auth/api-tokens/setup-link — one-time onboarding link for an
 *  agent (TTL 15 min, single use). The URL carries only an opaque code,
 *  no secret: the personal API token is issued at the moment the link
 *  is opened, and the response there is a self-contained markdown
 *  instruction with the token and the exact `claude mcp add` command. */
export interface SetupLinkResponse {
  url: string;
  expires_in_seconds: number;
  token_name: string;
}

export const apiTokensApi = {
  /** Own tokens, newest first. */
  list: () => request<{ items: ApiTokenRead[] }>("/auth/api-tokens"),
  create: (data: { name: string; expires_in_days?: number }) =>
    request<ApiTokenCreateResponse>("/auth/api-tokens", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  /** One-time onboarding link (requires api_token.issue). */
  setupLink: (data: { name: string; expires_in_days?: number }) =>
    request<SetupLinkResponse>("/auth/api-tokens/setup-link", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  /** Revoke a token by id. Owners revoke their own; holders of
   *  user.manage may revoke anyone's (used by the admin page). */
  revoke: (id: UUID) =>
    request<ApiTokenRead>(`/auth/api-tokens/${id}`, { method: "DELETE" }),
  /** All operators' tokens — admin only (user.manage). */
  adminListAll: () =>
    request<{ items: ApiTokenAdminRead[] }>("/auth/api-tokens/all"),
};

// ─── Membrane bench tests (sidecar-модуль) ───────────────────────────
//
// Стендовые замеры мембран: мобила вносит, десктоп показывает таблицу +
// графики (admin → Испытания мембран).  Endpoint /membrane-tests,
// requireAuth (без отдельного permission).

export interface MembraneTestRead {
  id: UUID;
  membrane_model: string;
  area: number;
  feed_tds: number | null;
  feed_conductivity: number | null;
  temperature: number;
  pressure_in: number;
  pressure_out: number;
  concentrate_flow: number;
  permeate_flow: number;
  tds: number | null;
  permeate_pressure: number;
  conductivity: number | null;
  serial: string | null;
  pump_frequency: number | null;
  note: string | null;
  /** Сессия стенда, в рамках которой снят зачётный замер (null — замер
   *  внесён вне сессии, напр. до появления стенда). */
  session_id: UUID | null;
  created_by: UUID | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** Частичное редактирование замера: любое поле опционально (довносим
 *  пропущенное или исправляем ошибку). Nullable-поля очищаются через null. */
export interface MembraneTestUpdate {
  membrane_model?: string;
  area?: 400 | 440;
  feed_tds?: number | null;
  feed_conductivity?: number | null;
  temperature?: number;
  pressure_in?: number;
  pressure_out?: number;
  concentrate_flow?: number;
  permeate_flow?: number;
  tds?: number | null;
  permeate_pressure?: number;
  conductivity?: number | null;
  serial?: string | null;
  pump_frequency?: number | null;
  note?: string | null;
}

export const membraneTestsApi = {
  list: (
    params: {
      membrane_model?: string;
      session_id?: UUID;
      /** Окно по времени внесения замера (страница истории). */
      from?: ISODateTime;
      to?: ISODateTime;
      page_size?: number;
    } = {},
  ) =>
    request<Page<MembraneTestRead>>(
      `/membrane-tests${qs({ page_size: 500, ...params })}`,
    ),
  models: () => request<{ models: string[] }>("/membrane-tests/models"),
  update: (id: UUID, data: MembraneTestUpdate) =>
    request<MembraneTestRead>(`/membrane-tests/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  remove: (id: UUID) =>
    request<void>(`/membrane-tests/${id}`, { method: "DELETE" }),
};

// ─── Membrane rig: сессии испытаний и телеметрия ─────────────────────
//
// Стенд мембран пишет непрерывную телеметрию (давление/температура) в
// рамках «сессии испытаний».  Сессию открывает оператор с мобилы или сам
// стенд (автостарт по появлению давления).  Зачётные замеры
// (/membrane-tests) привязываются к сессии через session_id.
// Endpoint /membrane-rig, requireAuth (без отдельного permission).

/** Кто открыл сессию: оператор с мобилы или сам стенд (автостарт). */
export type RigSessionStartedBy = "operator" | "rig";

/** Фильтр списка сессий: открытые (ended_at = null) / закрытые. */
export type RigSessionStatus = "open" | "closed";

export interface RigSessionRead {
  id: UUID;
  rig_id: string;
  started_by: RigSessionStartedBy;
  operator_id: UUID | null;
  started_at: ISODateTime;
  /** null — сессия ещё идёт. */
  ended_at: ISODateTime | null;
  membrane_model: string | null;
  area: number | null;
  serial: string | null;
  feed_tds: number | null;
  feed_conductivity: number | null;
  note: string | null;
  /** Итоги испытания, записанные при стопе (null, пока сессия идёт).
   *  Пики расхода — «храповик» прогнозного суточного: м³/сут = л/мин × 1,44. */
  p_mpa_avg: number | null;
  t_c_avg: number | null;
  q1_lpm_last: number | null;
  q2_lpm_last: number | null;
  q1_lpm_peak: number | null;
  q2_lpm_peak: number | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** Агрегаты по телеметрии сессии.  Давление — в МПа (в интерфейсе
 *  испытаний показываем бары = МПа × 10).  null — данных нет.
 *  Расходы, л/мин: последний мгновенный и пик за сессию (подача q1, пермеат q2). */
export interface RigSessionStats {
  samples: number;
  p_min: number | null;
  p_max: number | null;
  p_avg: number | null;
  t_min: number | null;
  t_max: number | null;
  t_avg: number | null;
  q1_lpm_last: number | null;
  q2_lpm_last: number | null;
  q1_lpm_peak: number | null;
  q2_lpm_peak: number | null;
}

/** Карточка сессии = сама сессия + агрегаты. */
export interface RigSessionDetail extends RigSessionRead {
  stats: RigSessionStats;
}

/** Точка агрегированного ряда телеметрии.  p_mpa/t_c = null означает, что
 *  датчик был в ошибке на всём бакете — на графике это РАЗРЫВ линии, а не
 *  ноль и не интерполяция.  n — сколько сырых отсчётов легло в бакет.
 *  v1..v3 — показание счётчика на конец бакета (сырой счёт импульсов),
 *  q1..q3_lpm — мгновенный расход, л/мин: сервер считает его из дельт
 *  счётчиков и весов импульсов (клиент про веса не знает). */
export interface RigSamplePoint {
  ts: ISODateTime;
  p_mpa: number | null;
  t_c: number | null;
  v1: number | null;
  v2: number | null;
  v3: number | null;
  q1_lpm: number | null;
  q2_lpm: number | null;
  q3_lpm: number | null;
  n: number;
}

export interface RigSamplesResponse {
  rig_id: string;
  from: ISODateTime;
  to: ISODateTime;
  /** Ширина бакета усреднения, подобранная сервером под max_points. */
  bucket_seconds: number;
  points: RigSamplePoint[];
}

/** Последний сэмпл стенда: живые значения для дашборда/мобилы.
 *  Онлайн/офлайн определяется по age_seconds (едж пушит раз в ~5 с). */
export interface RigLatest {
  rig_id: string;
  ts: ISODateTime;
  p_mpa: number;
  t_c: number;
  v1: number;
  v2: number;
  v3: number;
  ai_status: number[];
  age_seconds: number;
}

/** Настройки стенда: веса импульсов расходомеров (м³/имп) и счётчик,
 *  по которому дашборд показывает «мгновенный расход».  Меняются на лету —
 *  сервер применяет веса на чтении ряда.  updated_at = null — настройки ещё
 *  не сохранялись, действуют дефолты. */
export interface RigConfig {
  rig_id: string;
  v1_m3_per_pulse: number;
  v2_m3_per_pulse: number;
  v3_m3_per_pulse: number;
  flow_source: "v1" | "v2" | "v3";
  updated_at: ISODateTime | null;
}

export const membraneRigApi = {
  sessions: (
    params: {
      page?: number;
      page_size?: number;
      rig_id?: string;
      status?: RigSessionStatus;
    } = {},
  ) => request<Page<RigSessionRead>>(`/membrane-rig/sessions${qs(params)}`),
  session: (id: UUID) =>
    request<RigSessionDetail>(`/membrane-rig/sessions/${id}`),
  /** Ряд либо по session_id (интервал сессии), либо по rig_id + from/to
   *  (скользящее окно дашборда).  max_points: 50..2000, по умолчанию 1000
   *  (сервер сам выберет бакет). */
  samples: (
    params:
      | { session_id: UUID; max_points?: number }
      | { rig_id: string; from: ISODateTime; to?: ISODateTime; max_points?: number },
  ) => request<RigSamplesResponse>(`/membrane-rig/samples${qs(params)}`),
  latest: (rigId: string) =>
    request<RigLatest>(`/membrane-rig/latest${qs({ rig_id: rigId })}`),
  config: (rigId: string) =>
    request<RigConfig>(`/membrane-rig/config${qs({ rig_id: rigId })}`),
  /** Киоск: обмен общего пароля просмотра на read-only токен.
   *  404 = вход по паролю не настроен на сервере, 401 = пароль неверный. */
  kioskLogin: (password: string) =>
    request<{ token: string }>(`/membrane-rig/kiosk-login`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  saveConfig: (cfg: Omit<RigConfig, "updated_at">) =>
    request<RigConfig>(`/membrane-rig/config`, {
      method: "PUT",
      body: JSON.stringify(cfg),
    }),
};

// ---------- справочники установки ----------

export const referenceApi = {
  list: (list: ReferenceList) => request<ReferenceItem[]>(`/reference/${list}`),
  upsert: (list: ReferenceList, code: string, data: { label: Record<string, string>; sort_order?: number }) =>
    request<ReferenceItem>(`/admin/reference/${list}/${encodeURIComponent(code)}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  remove: (list: ReferenceList, code: string) =>
    request<void>(`/admin/reference/${list}/${encodeURIComponent(code)}`, { method: "DELETE" }),
};

// ---------- профиль компании ----------
export interface CompanyProfile {
  name: string;
  app_title: string;
  requisites: string;
  contact_email: string | null;
  contact_phone: string | null;
  mail_from: string | null;
  brand_color: string;
  passport_text_color: string;
  letterhead_svg: string | null;
  updated_at: string;
}

export const companyProfileApi = {
  get: () => request<CompanyProfile>("/admin/company-profile"),
  update: (patch: Partial<Omit<CompanyProfile, "updated_at">>) =>
    request<CompanyProfile>("/admin/company-profile", { method: "PATCH", body: JSON.stringify(patch) }),
};
